import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageIcon, Upload, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ImageBlock as ImageBlockType } from "@/canvas/types/blocks";
import { useCanvasStore } from "../../store";
import { uploadReportImage, getReportImageUrl, deleteReportImage } from "@/services/storage";
import { cn } from "@/lib/utils";

interface ImageBlockProps {
  block: ImageBlockType;
  readOnly?: boolean;
}

export function ImageBlock({ block, readOnly = false }: ImageBlockProps) {
  const { t } = useTranslation();
  const document = useCanvasStore((state) => state.document);
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasImage = Boolean(block.content.storagePath);

  // Fetch signed URL when storagePath changes
  useEffect(() => {
    if (!block.content.storagePath) {
      setImageUrl(null);
      return;
    }

    let cancelled = false;

    const fetchUrl = async () => {
      const url = await getReportImageUrl(block.content.storagePath);
      if (!cancelled) {
        setImageUrl(url);
        if (!url) {
          setError(t("canvas.blocks.image.error"));
        }
      }
    };

    fetchUrl();

    return () => {
      cancelled = true;
    };
  }, [block.content.storagePath, t]);

  // Handle click outside to exit edit mode
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    globalThis.document.addEventListener("mousedown", handleClickOutside);
    return () => globalThis.document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing]);

  // Upload handler wrapped in useCallback for dependency tracking
  const handleUpload = useCallback(
    async (file: File) => {
      if (!document?.id) {
        setError(t("canvas.blocks.image.error"));
        return;
      }

      setIsUploading(true);
      setError(null);

      const storagePath = await uploadReportImage(file, document.id);

      if (storagePath) {
        // Delete old image if replacing
        if (block.content.storagePath) {
          await deleteReportImage(block.content.storagePath);
        }

        updateBlockContent(block.id, {
          type: "image",
          ...block.content,
          storagePath,
        });
      } else {
        setError(t("canvas.blocks.image.error"));
      }

      setIsUploading(false);
    },
    [document?.id, block.content, block.id, updateBlockContent, t],
  );

  // Handle paste event for clipboard images
  useEffect(() => {
    if (!isEditing) return;

    const handlePaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await handleUpload(file);
          }
          break;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [isEditing, handleUpload]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input so same file can be selected again
    event.target.value = "";
  };

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);

      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) {
        await handleUpload(file);
      }
    },
    [handleUpload],
  );

  const handleRemoveImage = async () => {
    if (block.content.storagePath) {
      await deleteReportImage(block.content.storagePath);
    }

    updateBlockContent(block.id, {
      type: "image",
      storagePath: "",
      altText: undefined,
      caption: undefined,
    });
  };

  const handleAltTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateBlockContent(block.id, {
      type: "image",
      ...block.content,
      altText: event.target.value,
    });
  };

  const handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateBlockContent(block.id, {
      type: "image",
      ...block.content,
      caption: event.target.value,
    });
  };

  const handlePreviewClick = useCallback(() => {
    if (readOnly) return;
    setIsEditing(true);
  }, [readOnly]);

  // Edit mode - show upload area and controls (not available in readOnly mode)
  if (isEditing && !readOnly) {
    return (
      <div ref={containerRef} className="relative">
        {hasImage && imageUrl ? (
          <div className="space-y-4">
            {/* Image preview */}
            <div className="relative">
              <img
                src={imageUrl}
                alt={block.content.altText || ""}
                className="max-w-full h-auto rounded-md"
              />
            </div>

            {/* Image controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("canvas.blocks.image.replace")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveImage}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t("canvas.blocks.image.remove")}
              </Button>
            </div>

            {/* Alt text and caption */}
            <div className="grid gap-3">
              <div className="space-y-1">
                <Label htmlFor={`alt-${block.id}`} className="text-xs text-muted-foreground">
                  {t("canvas.blocks.image.altText")}
                </Label>
                <Input
                  id={`alt-${block.id}`}
                  value={block.content.altText || ""}
                  onChange={handleAltTextChange}
                  placeholder={t("canvas.blocks.image.altTextPlaceholder")}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`caption-${block.id}`} className="text-xs text-muted-foreground">
                  {t("canvas.blocks.image.caption")}
                </Label>
                <Input
                  id={`caption-${block.id}`}
                  value={block.content.caption || ""}
                  onChange={handleCaptionChange}
                  placeholder={t("canvas.blocks.image.captionPlaceholder")}
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "flex flex-col items-center justify-center p-8 transition-colors",
              isDragOver ? "border-primary bg-primary/5" : "",
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t("canvas.blocks.image.uploading")}
                </p>
              </>
            ) : (
              <>
                <ImageIcon className="h-8 w-8 text-muted-foreground opacity-50 mb-2" />
                <p className="text-sm text-muted-foreground text-center mb-2">
                  {t("canvas.blocks.image.dragDrop")}
                </p>
                <p className="text-xs text-muted-foreground/70 mb-4">
                  {t("canvas.blocks.image.paste")}
                </p>
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  {t("canvas.blocks.image.upload")}
                </Button>
              </>
            )}

            {error && <p className="text-sm text-destructive mt-2">{error}</p>}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  // Preview mode - show image or placeholder
  // When readOnly, no hover effects or click handler (image is still viewable)
  return (
    <div
      className={cn("relative rounded-md transition-colors", !readOnly && "cursor-pointer")}
      onClick={readOnly ? undefined : handlePreviewClick}
    >
      {hasImage && imageUrl ? (
        <figure>
          <img
            src={imageUrl}
            alt={block.content.altText || ""}
            className="max-w-full h-auto rounded-md"
          />
          {block.content.caption && (
            <figcaption className="mt-2 text-sm text-muted-foreground text-center">
              {block.content.caption}
            </figcaption>
          )}
        </figure>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
          <ImageIcon className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm italic">{t("canvas.blocks.image.dragDrop")}</p>
        </div>
      )}
    </div>
  );
}
